import type React from 'react';
import { getCurrencySymbol } from '../../../utils/currency';

/** US phone number auto-formatter. Converts raw input to (XXX) XXX-XXXX. */
export function formatUSPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** Returns true if the value contains exactly 10 digits (valid US number). */
export function isValidUSPhone(val: string): boolean {
  return val.replace(/\D/g, '').length === 10;
}

/** Returns true if the trimmed value is non-empty but has no letters or digits. */
export function hasOnlySpecialChars(val: string): boolean {
  return val.trim().length > 0 && !/[a-zA-Z0-9]/.test(val.trim());
}

/**
 * Strips characters that can't appear in a person's name — digits and symbols
 * other than space, apostrophe, hyphen, and period (e.g. "O'Brien", "Mary-Jane",
 * "St. John") — so First/Last Name fields silently reject them as you type,
 * matching the strip-while-typing behavior `limitDigits` applies to numeric fields.
 */
export function sanitizeNameInput(raw: string): string {
  return raw.replace(/[^a-zA-Z\s'.-]/g, '');
}

/** Standard email regex — rejects special chars like # outside quoted strings. */
export const EMAIL_RE = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;

/** Returns true if the email string passes the standard format check. */
export function isValidEmail(val: string): boolean {
  return EMAIL_RE.test(val.trim());
}

/**
 * Shared character limits for catering form inputs. Text fields apply these
 * through `maxLength`; numeric fields go through `limitDigits` instead, since
 * `maxLength` is ignored on `<input type="number">`.
 */
export const FIELD_LIMITS = {
  name: 25,
  phone: 14, // formatted "(555) 123-4567" — formatUSPhone caps the raw digits at 10
  email: 50,
  company: 50,
  address: 100,
  eventName: 100,
  /** Long-form notes that land in a TEXT column: internal notes, instructions, contract terms. */
  note: 500,
  itemDetails: 100,

  // ── Short notes, capped by the width of the column they are stored in ──

  /** Line item note. Column: mhc_line_items.notes VARCHAR(255). */
  lineItemNote: 250,
  /** Payment and refund notes. Column: mhc_payments.notes VARCHAR(250). */
  paymentNote: 250,
  /**
   * Lead "Mark as Lost" and estimate reject notes. Stored raw in
   * mhc_status_log.comments VARCHAR(100).
   */
  statusNote: 100,
  /**
   * Order cancellation note. Also stored in mhc_status_log.comments VARCHAR(100),
   * but the backend JSON-wraps it as {reason, note, refundDisposition} first.
   * That envelope costs 53–80 chars depending on which reason and refund
   * disposition are chosen, so only 20 chars are guaranteed to fit.
   */
  cancelNote: 20,
  deliveryInstructions: 200,
  /** Email/SMS & SMS Templates settings — Email Body (rich HTML template content). */
  emailTemplateBody: 8000,
} as const;

/** Digit budgets for numeric inputs, applied via `limitDigits`. */
export const DIGIT_LIMITS = {
  currency: 9,
  headcount: 6,
  qty: 5,
  days: 3,
  ordersPerDay: 5,
  dayOfMonth: 2,
  expiryDays: 3,
} as const;

/**
 * Caps a numeric input string to `maxDigits` digit characters and strips any
 * non-numeric character (sign, exponent, extra decimal points) so these
 * always-non-negative fields can't accept "-5", "4e4", or "1.2.3". Typing an
 * invalid character is a silent no-op, matching the existing digit-cap behavior.
 */
export function limitDigits(raw: string, maxDigits: number): string {
  if (raw === '') return raw;
  let out = raw.replace(/[^\d.]/g, '');
  const firstDot = out.indexOf('.');
  if (firstDot !== -1) {
    out = out.slice(0, firstDot + 1) + out.slice(firstDot + 1).replace(/\./g, '');
  }
  while ((out.match(/\d/g) || []).length > maxDigits) {
    out = out.slice(0, -1);
  }
  return out;
}

/** Upper bound for a percentage discount. */
export const MAX_DISCOUNT_PERCENT = 100;

export function maxDiscountValue(type: 'none' | 'percentage' | 'fixed', discountableTotal: number): number {
  if (type === 'percentage') return MAX_DISCOUNT_PERCENT;
  return Math.max(0, Math.round(discountableTotal * 100) / 100);
}

/** Human-readable "Max ..." hint shown under the discount input. */
export function discountMaxHint(type: 'none' | 'percentage' | 'fixed', discountableTotal: number): string {
  if (type === 'percentage') return `Max ${MAX_DISCOUNT_PERCENT}%`;
  return `Max ${formatMoney(maxDiscountValue(type, discountableTotal))}`;
}

export function validateDiscountValue(
  type: 'none' | 'percentage' | 'fixed',
  value: string,
  discountableTotal: number,
): string | undefined {
  if (type === 'none') return undefined;
  const raw = (value ?? '').trim();
  if (raw === '') return undefined;
  const n = Number(raw);
  if (Number.isNaN(n)) return undefined;
  const max = maxDiscountValue(type, discountableTotal);
  if (n <= max) return undefined;
  return type === 'percentage'
    ? `Discount cannot exceed ${MAX_DISCOUNT_PERCENT}%`
    : `Discount cannot exceed the line total of ${formatMoney(max)}`;
}

function formatMoney(n: number): string {
  return getCurrencySymbol() + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function stripLeadingZeros(raw: string): string {
  if (!raw) return raw;
  const negative = raw.startsWith('-');
  const unsigned = negative ? raw.slice(1) : raw;
  const dotIndex = unsigned.indexOf('.');
  const intPart = dotIndex === -1 ? unsigned : unsigned.slice(0, dotIndex);
  const rest = dotIndex === -1 ? '' : unsigned.slice(dotIndex);
  const strippedInt = intPart.replace(/^0+(?=\d)/, '');
  return `${negative ? '-' : ''}${strippedInt}${rest}`;
}

export function normalizeNumberInputOnBlur(e: React.FocusEvent<HTMLInputElement>): void {
  const raw = e.currentTarget.value;
  if (raw === '') return;
  const n = Number(raw);
  if (!Number.isNaN(n)) e.currentTarget.value = String(n);
}

/**
 * Blurs a focused number input on wheel scroll so the browser's default
 * scroll-to-change-value behavior can't silently mutate the field.
 */
export function blurOnWheel(e: React.WheelEvent<HTMLInputElement>): void {
  e.currentTarget.blur();
}
